import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Close
} from '@mui/icons-material';
import { colors } from '../../utils/colors';

export default function FileUpload({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  fullWidth = true,
  error = false,
  helperText,
  accept = '*',
  ...otherProps
}) {
  const [fileName, setFileName] = useState('');
  const inputId = id || `${label?.toLowerCase().replace(/\s+/g, '-')}-file`;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      if (onChange) {
        onChange(file);
      }
    }
  };

  const handleRemove = () => {
    setFileName('');
    if (onChange) {
      onChange(null);
    }
    // Reset the input
    const input = document.getElementById(inputId);
    if (input) {
      input.value = '';
    }
  };

  return (
    <FormControl variant="standard" fullWidth={fullWidth} error={error}>
      {label && (
        <InputLabel
          shrink
          sx={{
            fontSize: '1.2rem',
            fontWeight: 500,
            color: error ? 'error.main' : 'inherit',
            position: 'relative',
            transform: 'none',
            mb: 1
          }}
        >
          {label}
          {required && <span style={{ color: 'red' }}>*</span>}
        </InputLabel>
      )}

      <Box
        sx={{
          mt: label ? 3 : 0,
          border: `2px dashed ${error ? colors.error.main : '#E0E3E7'}`,
          borderRadius: 2,
          bgcolor: '#F3F6F9',
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: disabled ? '#E0E3E7' : colors.primary.main,
            bgcolor: disabled ? '#F3F6F9' : '#f8f9fa'
          }
        }}
        onClick={() => !disabled && !fileName && document.getElementById(inputId)?.click()}
      >
        <input
          id={inputId}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled}
          required={required}
          style={{ display: 'none' }}
          {...otherProps}
        />

        {fileName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Description sx={{ color: colors.primary.main, fontSize: 40 }} />
            <Box sx={{ flex: 1, textAlign: 'left' }}>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.primary,
                  fontWeight: 600,
                  wordBreak: 'break-word'
                }}
              >
                {fileName}
              </Typography>
            </Box>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              size="small"
              sx={{
                bgcolor: colors.error.light,
                color: colors.error.main,
                '&:hover': {
                  bgcolor: colors.error.main,
                  color: '#fff'
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
            <CloudUpload sx={{ fontSize: 48, color: colors.primary.main, mb: 1 }} />        
            <Typography
              variant="body2"
              sx={{
                color: colors.text.primary,
                fontWeight: 500,
                mb: 0.5
              }}
            >
              Tap to add document
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary
              }}
            >
              Upload your file here
            </Typography>
          </>
        )}
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? colors.error.main : colors.text.secondary,
            mt: 0.5,
            ml: 1.75,
            display: 'block'
          }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
}
